import { useEffect, useCallback, useRef } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { supabase } from "@/infra/supabase/client";
import { DASHBOARD_TOUR_STEPS } from "@/config/tour-steps";

export function useProductTour() {
  const tourRef = useRef<ReturnType<typeof driver> | null>(null);

  const checkTourCompleted = useCallback(async (): Promise<boolean> => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return true;

    const { data: milestone } = await supabase
      .from("system_milestones")
      .select("id")
      .eq("milestone_key", "tour_completed")
      .single();

    if (!milestone) return true;

    const { data: progress } = await supabase
      .from("user_milestone_progress")
      .select("completed")
      .eq("user_id", user.id)
      .eq("milestone_id", milestone.id)
      .maybeSingle();

    return progress?.completed === true;
  }, []);

  const markTourCompleted = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const { data: milestone } = await supabase
      .from("system_milestones")
      .select("id")
      .eq("milestone_key", "tour_completed")
      .single();

    if (!milestone) return;

    await supabase.from("user_milestone_progress").upsert(
      {
        user_id: user.id,
        milestone_id: milestone.id,
        completed: true,
        completed_at: new Date().toISOString(),
      },
      { onConflict: "user_id,milestone_id" },
    );
  }, []);

  const startTour = useCallback(() => {
    const driverObj = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayColor: "rgba(0, 0, 0, 0.6)",
      stagePadding: 8,
      stageRadius: 12,
      popoverClass: "help-tour-popover",
      nextBtnText: "Proximo",
      prevBtnText: "Anterior",
      doneBtnText: "Concluir",
      progressText: "{{current}} de {{total}}",
      steps: DASHBOARD_TOUR_STEPS,
      onDestroyStarted: () => {
        markTourCompleted();
        driverObj.destroy();
      },
    });

    tourRef.current = driverObj;
    driverObj.drive();
  }, [markTourCompleted]);

  // Auto-start on first access
  useEffect(() => {
    const timer = setTimeout(async () => {
      const completed = await checkTourCompleted();
      if (!completed) {
        startTour();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [checkTourCompleted, startTour]);

  // Listen for restart event from FAB
  useEffect(() => {
    const handleRestart = () => startTour();
    window.addEventListener("restart-product-tour", handleRestart);
    return () =>
      window.removeEventListener("restart-product-tour", handleRestart);
  }, [startTour]);

  return { startTour };
}
